import { usePageDescription, usePageTitle } from '@/lib/nav/document-title';
import Link from '@/lib/nav/link';

export function HowWeMakeMoneyPage() {
  usePageTitle('How we make money');
  usePageDescription(
    'Roam Together earns commission when you book through our recommendations. Here is exactly how it works.',
  );
  return (
    <>
      <header className="not-prose mb-10 text-center">
        <h1 className="font-display text-4xl font-bold tracking-tight md:text-5xl">
          How we make money
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg leading-7 text-ink/65">
          Short version: paid plans fund trip planning and Ask Roam usage, and when you book a
          flight, hotel, tour, eSIM, or insurance through our recommendations, we may earn a small
          commission from the partner. Affiliate links never change the price you pay.
        </p>
      </header>

      <section className="space-y-4">
        <h2 className="font-display text-2xl font-bold">The longer version</h2>
        <p>
          Roam Together offers a free first-trip experience, then paid plans for families who want
          to keep planning. Those plans include monthly Ask Roam usage; if a family uses more, they
          can add prepaid usage before another AI call starts. When our AI recommends a specific
          flight, hotel, restaurant booking, or experience, we may generate an affiliate-tagged link
          to the partner that sells it. We are not generating affiliate links yet; when we start, we
          will name the partners here before we do.
        </p>
        <p>
          If you click through and complete a booking, the partner pays us a commission — typically
          a few percent of the booking value. For a $500 flight we might earn $5–$10. For an $80
          activity we might earn $6–$10. For an eSIM it&apos;s a higher percentage but lower dollar
          amount.
        </p>
        <p>
          <strong className="font-semibold">Your price is identical either way.</strong> The
          commission comes out of the partner&apos;s margin, not yours. We don&apos;t mark anything
          up. You can always shop the same booking by searching the partner&apos;s site directly —
          we&apos;re simply earning a finder&apos;s fee for having recommended it.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="font-display text-2xl font-bold">What this does and doesn&apos;t change</h2>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong className="font-semibold">
              Recommendations are based on family fit, not commission.
            </strong>{' '}
            The Advisor and Activity Filler prompts only know about your family profile, your
            destination, and what makes sense for the trip. They don&apos;t see commission rates.
          </li>
          <li>
            <strong className="font-semibold">
              We surface higher-margin categories when it makes sense
            </strong>{' '}
            — things like eSIMs for international trips, travel insurance for longer or adventurous
            trips. Those are also the things families most need. If your trip doesn&apos;t warrant
            them, you won&apos;t see them.
          </li>
          <li>
            <strong className="font-semibold">We track what you change.</strong> When you swap out
            something the AI suggested, we log what we said versus what you picked. That training
            data gradually makes our recommendations better. It&apos;s pseudonymous and disclosed in
            our{' '}
            <Link href="/privacy" className="font-medium text-primary hover:underline">
              Privacy Policy
            </Link>
            .
          </li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="font-display text-2xl font-bold">How affiliate links work</h2>
        <p>
          On the pages where we show trip details, booking buttons may use affiliate-tagged links
          from our affiliate partners. If you click one of those booking links and complete a
          purchase, we may receive credit for the referral.
        </p>
        <p>
          We keep that behavior limited to intentional recommendation links, such as flight,
          transport, ticket, eSIM, or insurance buttons. App navigation like My Trips, Dashboard,
          Settings, and itinerary tabs should always stay inside Roam Together.
        </p>
        <p>
          <strong className="font-semibold">Your price doesn&apos;t change.</strong> The provider
          destination and price are the same; the affiliate marker just tells the partner that Roam
          Together referred the visit.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="font-display text-2xl font-bold">Can I turn it off?</h2>
        <p>
          The commission layer isn&apos;t something you opt in or out of — it&apos;s how the product
          pays for itself. But you can absolutely book outside our links. Every recommendation shows
          the venue name and address; copy it, go to the partner site directly, book there.
          You&apos;ll pay the same price, and we just won&apos;t earn a commission on that specific
          booking. No hard feelings.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="font-display text-2xl font-bold">Questions?</h2>
        <p>
          Email{' '}
          <a
            href="mailto:hello@roamtogether.com"
            className="font-medium text-primary hover:underline"
          >
            hello@roamtogether.com
          </a>
          . We&apos;d rather explain than hide anything.
        </p>
      </section>

      <footer className="pt-8 text-sm text-muted-foreground">
        <p>
          <Link href="/privacy" className="hover:underline">
            Privacy Policy
          </Link>
          {' · '}
          <Link href="/terms" className="hover:underline">
            Terms of Service
          </Link>
        </p>
      </footer>
    </>
  );
}
